import { LookupCell } from '@/components/BsrsGrid/lookupCell'
import { getStore } from '@/store'
import { formatDate, defaultDateTimeFormat } from '@/utils/dateUtilities'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import pdfFont from '@/styles/pdfFont'
import { DateCell, DateTimeCell } from '@/components/BsrsGrid/dateCells'
import { fixDataState } from '@/components/BsrsGrid/dataProviders'
import debug from 'debug'
import _ from 'lodash'

const log = debug('app:grid:export')

export const ExportType = {
  PDF: 0,
  XLS: 1,
  DATA: 2
}

export function exportGrid ({ exportType, dataSource, items, dataState, columns, excelExporter, useServerProcessing, fontSize, paperSize, sheetHeader, localizeProps, localize }) {
  if (useServerProcessing) {
    const responsePromise = dataSource(fixDataState({ ...dataState, take: 0, page: 0 }, getColumnTypes(columns), {
      localizeProps: localizeProps
    }))
    if (exportType !== ExportType.DATA) {
      responsePromise
        .then(response => {
          processExport(exportType, excelExporter, response.data, columns, fontSize, paperSize, sheetHeader, localizeProps, localize)
        }).catch((e) => log('ERROR: %o', e))
    } else {
      return responsePromise
    }
  } else {
    processExport(exportType, excelExporter, items, columns, fontSize, paperSize, sheetHeader, localizeProps, localize)
  }
}

function exportExcel (exporter, data, columns, sheetHeader, localizeProps, localize) {
  const transformed = excelDataColumnTransform(data, columns, localizeProps, localize)

  const options = exporter.workbookOptions(transformed.data, transformed.columns)

  // Apply additional formatting to each row based on _ members
  for (let i = 0; i < transformed.data.length; i++) {
    const keys = Object.keys(transformed.data[i]).filter(x => x.startsWith('_'))

    // Remove the _ in front of the wanted members
    const modifiedProperties =
      Object.fromEntries(
        Object.entries(_.pick(transformed.data[i], keys))
          .map(([key, value]) => [key.replace('_', ''), value])
      )
    options.sheets[0].rows[i + 1] = {
      ...options.sheets[0].rows[i + 1],
      ...modifiedProperties
    }
  }

  // Add header row
  if (!sheetHeader) {
    options.sheets[0].rows.unshift({
      type: 'header',
      cells: [{ value: sheetHeader, bold: true, background: '#7a7a7a', color: '#fff' }]
    })
  }
  exporter.save(options)
}

function exportPdf (data, columns, fontSize, paperSize, sheetHeader, localizeProps, localize) {
  const transformed = pdfDataColumnTransform(data, columns, localizeProps, localize)

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: paperSize.toLowerCase()
  })
  const locale = getStore().getState().localization.locale

  const AmiriRegular = pdfFont
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegular)
  doc.addFont('Amiri-Regular.ttf', 'custom', 'normal')

  doc.setFont('custom')

  const insertHeader = sheetHeader
    ? data => {
      doc.setFontSize(fontSize + 5)
      doc.setFontStyle('bold')
      doc.text(sheetHeader, data.settings.margin.left, 50)
    }
    : null

  doc.autoTable({
    headStyles: { font: 'custom', fontStyle: 'normal', halign: (locale === 'ar' ? 'right' : 'left'), fontSize: fontSize },
    bodyStyles: { font: 'custom', halign: (locale === 'ar' ? 'right' : 'left'), fontSize: fontSize },
    margin: { top: sheetHeader ? 50 + fontSize + 5 : 40 },
    body: transformed.data,
    columns: transformed.columns,
    columnStyles: transformed.columnStyles,
    beforePageContent: insertHeader
  })

  doc.save('Export.pdf')
}

function processExport (exportType, exporter, data, columns, fontSize, paperSize, sheetHeader, localizeProps, localize) {
  columns = columns.filter(c => !c.notExport)
  switch (exportType) {
    case ExportType.PDF:
      exportPdf(data, columns, fontSize, paperSize, sheetHeader, localizeProps, localize)
      break
    case ExportType.XLS:
      exportExcel(exporter, data, columns, sheetHeader, localizeProps, localize)
      break
  }
}

// because of some columns have cell type LookupCell, data transformation has to be made
function excelDataColumnTransform (data, columns, localizeProps, localize) {
  let transformedData = []
  let transformedColumns = filterOutColumns(columns)
  const lookupKey = localizeProps()

  data.forEach(row => {
    let transformedObject = { ...deleteIdField(row, transformedColumns) }

    transformedColumns.forEach(column => {
      const key = column.field

      if (key) {
        if (typeof column.cellExport === 'function') {
          const res = column.cellExport({
            field: column.field,
            dataItem: row,
            localize: localize,
            additionalProps: column.cellExportProps
          })
          // Add additional row properties if they are present
          if (typeof res === 'object' && res.value) {
            const { value, ...additional } = res
            transformedObject[key] = value
            transformedObject = { ...transformedObject, ...additional }
          } else {
            transformedObject[key] = res
          }
        } else if (typeof column.cell === 'function' && column.cell === LookupCell && row[key] !== null) {
          transformedObject[key] = row[key] ? row[key][lookupKey] : ''
        } else if (column.cell === DateCell && row[key]) {
          transformedObject[key] = formatDate(row[key])
        } else if (column.cell === DateTimeCell && row[key]) {
          transformedObject[key] = formatDate(row[key], defaultDateTimeFormat)
        } else if (typeof row[key] === 'number' && column.enumName) {
          transformedObject[key] = getTranslatedEnum(column.enumName, row[key])
        } else if (typeof row[key] === 'boolean') {
          transformedObject[key] = transformedObject[key] === true ? localize('Common.Yes') : localize('Common.No')
        }

        // Fix up empty values
        if (transformedObject[key] === null || transformedObject[key] === undefined) {
          transformedObject[key] = ''
        }
      }
    })

    transformedData.push(transformedObject)
  })

  return {
    data: transformedData,
    columns: transformedColumns
  }
}

function pdfDataColumnTransform (data, columns, localizeProps, localize) {
  const locale = getStore().getState().localization.locale
  const lookupKey = localizeProps()
  let transformedData = []
  let transformedColumns = filterOutColumns(columns).map(x => ({
    dataKey: x.field,
    header: x.title
  }))

  if (locale === 'ar') {
    transformedColumns = transformedColumns.reverse()
  }

  let longColumns = []

  data.forEach(row => {
    let transformedObject = { ...deleteIdField(row, transformedColumns) }

    transformedColumns.forEach(column => {
      const gridColumn = columns.find(x => x.field === column.dataKey)
      const key = gridColumn.field

      if (key) {
        if (typeof gridColumn.cellExport === 'function') {
          transformedObject[key] = gridColumn.cellExport({
            field: gridColumn.field,
            dataItem: row,
            localize: localize,
            additionalProps: gridColumn.cellExportProps
          })
        } else if (typeof gridColumn.cell === 'function' && gridColumn.cell === LookupCell && row[key] !== null) {
          transformedObject[key] = row[key][lookupKey]
        } else if (gridColumn.cell === DateCell && row[key]) {
          transformedObject[key] = formatDate(row[key])
        } else if (gridColumn.cell === DateTimeCell && row[key]) {
          transformedObject[key] = formatDate(row[key], defaultDateTimeFormat)
        } else if (typeof row[key] === 'number' && gridColumn.enumName) {
          transformedObject[key] = getTranslatedEnum(gridColumn.enumName, transformedObject[key])
        } else if (typeof row[key] === 'boolean') {
          transformedObject[key] = transformedObject[key] === true ? localize('Common.Yes') : localize('Common.No')
        }

        if (transformedObject[key] && transformedObject[key].length && transformedObject[key].length > 30) {
          longColumns = [...longColumns, key]
        }
      }
    })

    transformedData.push(transformedObject)
  })

  let columnStyles = {}

  transformedColumns.forEach(function (item) {
    const column = columns.find(x => x.field === item.dataKey)

    if (column.cell === DateTimeCell) {
      columnStyles = { ...columnStyles, [item.dataKey]: { cellWidth: 90 } }
    } else if (column.cell === DateCell) {
      columnStyles = { ...columnStyles, [item.dataKey]: { cellWidth: 70 } }
    } else {
      if (longColumns.includes(item.dataKey)) {
        columnStyles = { ...columnStyles, [item.dataKey]: { overflow: 'linebreak' } }
      } else {
        columnStyles = { ...columnStyles, [item.dataKey]: { cellWidth: 'wrap' } }
      }
    }
  })

  return {
    data: transformedData,
    columns: transformedColumns,
    columnStyles: columnStyles
  }
}

function filterOutColumns (columns) {
  columns.forEach(c => {
    delete c.width
    delete c.headerCell
  })

  return columns.filter(x =>
    x.title !== ' ' &&
    (!x.cell || x.cell.omitFromExport !== true)
  )
}

function deleteIdField (o, columns) {
  if (!columns.find(x => x.field === 'id') && !columns.find(x => x.dataKey === 'id')) {
    delete o.id
    return o
  }

  return { ...o }
}

function getColumnTypes (columns) {
  const types = {}
  columns.forEach(x => {
    types[x.field] = getColumnType(x)
  })
  return types
}

function getColumnType (column) {
  if (column.cell === LookupCell) {
    return 'lookup'
  }
  return 'simple'
}

function getTranslatedEnum (enumName, value) {
  const localization = getStore().getState().localization

  return localization.data[`Enums.${enumName}_${value}`]
}



// WEBPACK FOOTER //
// ./src/components/BsrsGrid/BsrsGrid.Export.js